import { FC } from 'react'
import { KTIcon } from '../../../../_metronic/helpers'
import { formatNumber } from '../../../../reusable'
import { SponserStepCommonProps } from '../../types/sponser'

const BudgetPaymentStep: FC<SponserStepCommonProps> = ({ t, sponserData }) => {
  const amountPerDay = parseFloat(sponserData?.perDayPrice ?? '0') || 0
  const duration = sponserData?.duration ?? 0
  const durationType = sponserData?.durationType ?? ''
  const totalAmount = parseFloat(String(sponserData?.price ?? 0)) || amountPerDay * duration
  const paymentMethod = sponserData?.paymentMethod ?? ''
  const paymentStatus = sponserData?.paymentStatus ?? ''
  const transactionNumber = sponserData?.transactionNumber ?? ''

  const propertyName = sponserData?.en_name || '-'
  const parentName = sponserData?.en_parentName || ''
  const categoryName = sponserData?.en_categoryName || ''
  const actionType = sponserData?.actionType || ''

  const getStatusBadge = (status: string) => {
    if (status === 'PAID') return 'sponserform-badge sponserform-badge--success'
    if (status === 'FAILED') return 'sponserform-badge sponserform-badge--danger'
    if (status === 'PENDING') return 'sponserform-badge sponserform-badge--warning'
    return 'sponserform-badge sponserform-badge--muted'
  }

  return (
    <>
      <div className='mb-6'>
        <h4 className='fw-bold fs-4 mb-1'>{t('Budget & Payment')}</h4>
        <p className='text-gray-600 fs-7 mb-0'>{t('Billing and property details for this sponser')}</p>
      </div>

      {/* Property Details */}
      <div className='sponserform-billing-card'>
        <div className='sponserform-billing-card__header'>
          <KTIcon iconName='home-2' className='fs-2 text-primary' />
          <span className='fw-bold fs-5'>{t('Sponsored Property')}</span>
        </div>

        <div className='sponserform-billing-row'>
          <span className='sponserform-billing-row__label'>{t('Property Name')}</span>
          <span className='sponserform-billing-row__value fw-bold'>{parentName ? parentName : propertyName}</span>
        </div>
        {parentName && (
          <div className='sponserform-billing-row'>
            <span className='sponserform-billing-row__label'>{t('Listing Name')}</span>
            <span className='sponserform-billing-row__value'>{propertyName}</span>
          </div>
        )}
        {categoryName && (
          <div className='sponserform-billing-row'>
            <span className='sponserform-billing-row__label'>{t('Category')}</span>
            <span className='sponserform-billing-row__value'>{categoryName}</span>
          </div>
        )}
        {actionType && (
          <div className='sponserform-billing-row'>
            <span className='sponserform-billing-row__label'>{t('Property Type')}</span>
            <span className='sponserform-billing-row__value text-capitalize'>{actionType}</span>
          </div>
        )}
      </div>

      {/* Billing Summary */}
      <div className='sponserform-billing-card mt-5'>
        <div className='sponserform-billing-card__header'>
          <KTIcon iconName='clipboard' className='fs-2 text-primary' />
          <span className='fw-bold fs-5'>{t('Billing Summary')}</span>
        </div>

        <div className='sponserform-billing-row'>
          <span className='sponserform-billing-row__label'>{t('Pricing Model')}</span>
          <span className='sponserform-billing-row__value'>{t('Per Day')}</span>
        </div>
        <div className='sponserform-billing-row'>
          <span className='sponserform-billing-row__label'>{t('Rate Per Day')}</span>
          <span className='sponserform-billing-row__value'>{amountPerDay > 0 ? formatNumber(amountPerDay) : '—'}</span>
        </div>
        <div className='sponserform-billing-row'>
          <span className='sponserform-billing-row__label'>{t('Duration')}</span>
          <span className='sponserform-billing-row__value'>{duration > 0 ? `${duration} ${durationType}` : '—'}</span>
        </div>
        <div className='sponserform-billing-total'>
          <span className='fw-bold fs-4 text-gray-800'>{t('Total Amount')}</span>
          <span className='fw-bolder fs-3 text-primary'>{totalAmount > 0 ? formatNumber(totalAmount) : '—'}</span>
        </div>
      </div>

      {/* Payment Details */}
      {(paymentMethod || transactionNumber || paymentStatus) && (
        <div className='sponserform-billing-card mt-5'>
          <div className='sponserform-billing-card__header'>
            <KTIcon iconName='dollar' className='fs-2 text-success' />
            <span className='fw-bold fs-5'>{t('Payment Details')}</span>
          </div>

          {paymentMethod && (
            <div className='sponserform-billing-row'>
              <span className='sponserform-billing-row__label'>{t('Payment Method')}</span>
              <span className='sponserform-billing-row__value fw-bold text-capitalize'>{paymentMethod}</span>
            </div>
          )}
          {paymentStatus && (
            <div className='sponserform-billing-row'>
              <span className='sponserform-billing-row__label'>{t('Payment Status')}</span>
              <span className={getStatusBadge(paymentStatus)}>{paymentStatus}</span>
            </div>
          )}
          {transactionNumber && (
            <div className='sponserform-billing-row'>
              <span className='sponserform-billing-row__label'>{t('Transaction ID')}</span>
              <code className='sponserform-transaction-id'>{transactionNumber}</code>
            </div>
          )}
        </div>
      )}

      {/* No payment info */}
      {!paymentMethod && !transactionNumber && !paymentStatus && totalAmount === 0 && (
        <div className='sponserform-billing-empty'>
          <KTIcon iconName='information-5' className='fs-1 text-warning flex-shrink-0' />
          <div>
            <div className='fw-bold fs-6 text-warning mb-1'>{t('No Payment Information')}</div>
            <p className='text-gray-700 fs-7 mb-0'>{t('Payment details will appear here once the vendor completes the payment process.')}</p>
          </div>
        </div>
      )}
    </>
  )
}

export { BudgetPaymentStep }
